import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { Check, Download, Eye, Pencil, Plus, RefreshCw, Search, Settings2, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../components/ui/button';
import api from '../services/api';
import LoaderProgress from '../components/ui/loader-progress';
import DocumentPreviewDrawer from '../components/DocumentPreviewDrawer';
import PageIntroDialog from '../components/PageIntroDialog';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: '16px',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`
}));

const normalizeTipoForm = (value) => ({
  id: value?.id != null ? Number(value.id) : null,
  code: String(value?.code || '').trim(),
  label: String(value?.label || '').trim(),
  required: Boolean(value?.required),
  active: value?.active == null ? true : Boolean(value.active),
  sort_order: value?.sort_order != null ? Number(value.sort_order) : 0
});

export default function RichiesteDocumentiParametri() {
  const [introOpen, setIntroOpen] = useState(true);
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [allowIncomplete, setAllowIncomplete] = useState(false);

  const [defaultIssuer, setDefaultIssuer] = useState(null);
  const [issuerQuery, setIssuerQuery] = useState('');
  const [issuerOptions, setIssuerOptions] = useState([]);
  const [issuerLoading, setIssuerLoading] = useState(false);

  const [legalUploading, setLegalUploading] = useState(false);
  const legalDocFileRef = useRef(null);
  const [legalPreviewOpen, setLegalPreviewOpen] = useState(false);
  const [legalPreviewUrl, setLegalPreviewUrl] = useState('');
  const [legalPreviewLoading, setLegalPreviewLoading] = useState(false);
  const [legalPreviewOpenInNewTabLoading, setLegalPreviewOpenInNewTabLoading] = useState(false);

  const [tipi, setTipi] = useState([]);
  const [tipiLoading, setTipiLoading] = useState(false);
  const [onlyActive, setOnlyActive] = useState(true);
  const [search, setSearch] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [tipoDialogOpen, setTipoDialogOpen] = useState(false);
  const [tipoSaving, setTipoSaving] = useState(false);
  const [tipoForm, setTipoForm] = useState(normalizeTipoForm({}));

  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const res = await api.get('/richieste-doc/settings');
      const s = res.data?.settings || null;
      setSettings(s);
      setAllowIncomplete(Boolean(s?.allow_incomplete_generation));
      const defaultIssuerId = s?.default_issuer_id != null ? Number(s.default_issuer_id) : null;
      if (Number.isInteger(defaultIssuerId) && defaultIssuerId > 0) {
        try {
          const issuerRes = await api.get('/richieste-doc/issuers/search', { params: { q: String(defaultIssuerId) } });
          const opt = (issuerRes.data?.data || []).find((x) => Number(x?.id) === defaultIssuerId) || null;
          setDefaultIssuer(opt);
        } catch (_e) {
          setDefaultIssuer({ id: defaultIssuerId, name: `Issuer #${defaultIssuerId}` });
        }
      } else {
        setDefaultIssuer(null);
      }
    } catch (_error) {
      setSettings(null);
      setAllowIncomplete(false);
      setDefaultIssuer(null);
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveSettings = async () => {
    if (settingsSaving || legalUploading) return;
    try {
      setSettingsSaving(true);
      await api.put('/richieste-doc/settings', {
        allow_incomplete_generation: Boolean(allowIncomplete),
        default_issuer_id: defaultIssuer?.id != null ? Number(defaultIssuer.id) : null
      });
      toast.success('Impostazioni salvate');
      await fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore salvataggio impostazioni');
    } finally {
      setSettingsSaving(false);
    }
  };

  const pickLegalDocFile = () => {
    if (settingsSaving || legalUploading) return;
    legalDocFileRef.current?.click?.();
  };

  const uploadLegalDocFile = async (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!file) return;
    try {
      setLegalUploading(true);
      const form = new FormData();
      form.append('file', file);
      await api.post('/richieste-doc/settings/legal-doc', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documento legale aggiornato');
      await fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore upload documento legale');
    } finally {
      setLegalUploading(false);
    }
  };

  const clearLegalDocFile = async () => {
    if (settingsSaving || legalUploading) return;
    try {
      setLegalUploading(true);
      await api.delete('/richieste-doc/settings/legal-doc');
      toast.success('Documento legale rimosso');
      await fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore rimozione documento legale');
    } finally {
      setLegalUploading(false);
    }
  };

  const downloadLegalDocFile = async () => {
    try {
      const res = await api.get('/richieste-doc/settings/legal-doc/download', { responseType: 'blob' });
      const name = settings?.legal_doc_file_name || 'doc_identita_legale';
      const blob = new Blob([res.data], { type: settings?.legal_doc_mime_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore download documento legale');
    }
  };

  const setLegalPreviewUrlSafe = (nextUrl) => {
    setLegalPreviewUrl((prev) => {
      if (prev && prev.startsWith('blob:')) {
        window.URL.revokeObjectURL(prev);
      }
      return nextUrl || '';
    });
  };

  const ensureLegalPreviewUrl = async () => {
    if (legalPreviewUrl) return legalPreviewUrl;
    const res = await api.get('/richieste-doc/settings/legal-doc/download', { responseType: 'blob' });
    const blob = new Blob([res.data], { type: settings?.legal_doc_mime_type || 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    setLegalPreviewUrlSafe(url);
    return url;
  };

  const openLegalPreview = async () => {
    const legalDocNameLocal = String(settings?.legal_doc_file_name || '').trim();
    if (!legalDocNameLocal) return;
    setLegalPreviewOpen(true);
    if (legalPreviewUrl) return;
    try {
      setLegalPreviewLoading(true);
      await ensureLegalPreviewUrl();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore anteprima documento legale');
    } finally {
      setLegalPreviewLoading(false);
    }
  };

  const openLegalPreviewInNewTab = async () => {
    const legalDocNameLocal = String(settings?.legal_doc_file_name || '').trim();
    if (!legalDocNameLocal) return;
    try {
      setLegalPreviewOpenInNewTabLoading(true);
      const url = await ensureLegalPreviewUrl();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore apertura documento');
    } finally {
      setLegalPreviewOpenInNewTabLoading(false);
    }
  };

  useEffect(() => {
    if (!settings?.legal_doc_file_name) {
      setLegalPreviewUrlSafe('');
    }
  }, [settings?.legal_doc_file_name]);

  useEffect(() => {
    return () => {
      if (legalPreviewUrl && legalPreviewUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(legalPreviewUrl);
      }
    };
  }, [legalPreviewUrl]);

  const fetchTipi = async (activeOnlyArg = onlyActive) => {
    setTipiLoading(true);
    try {
      const res = await api.get('/richieste-doc/risposte/tipi', { params: { active: activeOnlyArg ? 1 : 0 } });
      setTipi(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore caricamento tipi');
      setTipi([]);
    } finally {
      setTipiLoading(false);
    }
  };

  const openNewTipo = () => {
    setTipoForm(normalizeTipoForm({ active: true, required: false, sort_order: 0 }));
    setTipoDialogOpen(true);
  };

  const openEditTipo = (row) => {
    setTipoForm(normalizeTipoForm(row));
    setTipoDialogOpen(true);
  };

  const closeTipoDialog = () => {
    if (tipoSaving) return;
    setTipoDialogOpen(false);
    setTipoForm(normalizeTipoForm({}));
  };

  const saveTipo = async () => {
    if (tipoSaving) return;
    const isEdit = Number.isInteger(tipoForm?.id) && tipoForm.id > 0;
    const code = String(tipoForm?.code || '').trim();
    const label = String(tipoForm?.label || '').trim();
    const sortOrder = Number(tipoForm?.sort_order || 0);
    if (!isEdit) {
      if (!code) {
        toast.error('Inserisci il code');
        return;
      }
      if (!/^[a-z0-9_]+$/.test(code)) {
        toast.error('Code non valido (usa solo a-z, 0-9, underscore)');
        return;
      }
    }
    if (!label) {
      toast.error('Inserisci la label');
      return;
    }

    try {
      setTipoSaving(true);
      if (isEdit) {
        await api.put(`/richieste-doc/risposte/tipi/${tipoForm.id}`, {
          label,
          required: Boolean(tipoForm.required),
          active: Boolean(tipoForm.active),
          sort_order: Number.isFinite(sortOrder) ? sortOrder : 0
        });
      } else {
        await api.post('/richieste-doc/risposte/tipi', {
          code,
          label,
          required: Boolean(tipoForm.required),
          active: Boolean(tipoForm.active),
          sort_order: Number.isFinite(sortOrder) ? sortOrder : 0
        });
      }
      toast.success(isEdit ? 'Tipo aggiornato' : 'Tipo creato');
      closeTipoDialog();
      await fetchTipi(onlyActive);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore salvataggio tipo');
    } finally {
      setTipoSaving(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchTipi(true);
  }, []);

  useEffect(() => {
    fetchTipi(onlyActive);
    setPage(0);
  }, [onlyActive]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      const q = String(issuerQuery || '').trim();
      if (!q || q.length < 2) {
        setIssuerOptions([]);
        setIssuerLoading(false);
        return;
      }
      setIssuerLoading(true);
      try {
        const res = await api.get('/richieste-doc/issuers/search', { params: { q } });
        setIssuerOptions(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (_e) {
        setIssuerOptions([]);
      } finally {
        setIssuerLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [issuerQuery]);

  const filteredTipi = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    const base = Array.isArray(tipi) ? tipi : [];
    if (!q) return base;
    return base.filter((t) => String(t?.code || '').toLowerCase().includes(q) || String(t?.label || '').toLowerCase().includes(q));
  }, [tipi, search]);

  const pagedTipi = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredTipi.slice(start, end);
  }, [filteredTipi, page, rowsPerPage]);

  const legalDocName = String(settings?.legal_doc_file_name || '').trim();
  const legalDocPresent = Boolean(legalDocName);
  const legalDocMimeType = String(settings?.legal_doc_mime_type || '').trim();
  const legalDocFormat = legalDocMimeType
    ? legalDocMimeType
    : legalDocName
        .split('.')
        .slice(-1)[0]
        ?.toUpperCase() || '';

  return (
    <Box sx={{ p: 3 }}>
      <PageIntroDialog
        open={introOpen}
        onClose={() => setIntroOpen(false)}
        title="Parametri"
        subtitle="Configura impostazioni e lookup utilizzati da Richieste Documenti."
        icon={Settings2}
        color="#2563eb"
        bullets={[
          'Documento identità legale: upload, anteprima e download',
          'Creditore di default (selezione rapida nelle richieste)',
          'Tipi documenti ricevuti: obbligatorietà, attivo e ordine'
        ]}
        example="Esempio: imposta come creditore di default “CRIF”, che verrà aggiunto come creditore a cui inoltrare una richiesta documenti."
      />
      <input ref={legalDocFileRef} type="file" onChange={uploadLegalDocFile} style={{ display: 'none' }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#2563eb14',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Settings2 size={20} color="#2563eb" />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
              Parametri
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tipi documenti ricevuti e impostazioni di Richieste Documenti
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button variant="outline" onClick={() => fetchTipi(onlyActive)} title="Ricarica">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={openNewTipo}>
            <Plus className="mr-2 h-4 w-4" />
            Nuovo tipo
          </Button>
        </Box>
      </Box>

      <StyledCard sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Impostazioni Richieste Documenti
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configurazione globale (documento identità legale, creditore di default, gestione incompleti)
              </Typography>
            </Box>
            <Button onClick={saveSettings} disabled={settingsSaving || legalUploading}>
              <Check className="mr-2 h-4 w-4" />
              Salva
            </Button>
          </Box>

          {settingsLoading ? (
            <LoaderProgress />
          ) : (
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid item xs={12} md={5}>
                <FormControlLabel
                  control={<Checkbox checked={allowIncomplete} onChange={(e) => setAllowIncomplete(e.target.checked)} />}
                  label="Consenti generazione anche se incompleta"
                />
              </Grid>
              <Grid item xs={12} md={7}>
                <Autocomplete
                  value={defaultIssuer}
                  onChange={(_e, newValue) => setDefaultIssuer(newValue)}
                  options={issuerOptions}
                  loading={issuerLoading}
                  getOptionLabel={(o) => String(o?.name || o?.pec || '')}
                  isOptionEqualToValue={(o, v) => Number(o?.id) === Number(v?.id)}
                  onInputChange={(_e, value) => setIssuerQuery(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Creditore di default"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {issuerLoading ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    p: 1.25,
                    borderRadius: '16px',
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.mode === 'dark' ? alpha('#0b1220', 0.6) : '#f8fafc'
                  })}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, mr: 0.5 }}>
                    Doc. identità legale:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#16a34a' }}>
                    {legalDocPresent ? legalDocName : '—'}
                  </Typography>

                  <Box sx={{ flex: 1 }} />

                  <Tooltip title="Visualizza" arrow>
                    <span>
                      <IconButton size="small" onClick={openLegalPreview} disabled={!legalDocPresent || legalUploading}>
                        <Eye size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Carica" arrow>
                    <span>
                      <IconButton size="small" onClick={pickLegalDocFile} disabled={settingsSaving || legalUploading}>
                        <Upload size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Scarica" arrow>
                    <span>
                      <IconButton size="small" onClick={downloadLegalDocFile} disabled={!legalDocPresent || legalUploading}>
                        <Download size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Rimuovi" arrow>
                    <span>
                      <IconButton size="small" onClick={clearLegalDocFile} disabled={!legalDocPresent || legalUploading}>
                        <Trash2 size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </StyledCard>

      <DocumentPreviewDrawer
        open={legalPreviewOpen}
        onClose={() => setLegalPreviewOpen(false)}
        fileName={legalDocName || ''}
        fileUrl={legalPreviewUrl || ''}
        loading={legalPreviewLoading}
        onOpenInNewTab={openLegalPreviewInNewTab}
        openInNewTabLoading={legalPreviewOpenInNewTabLoading}
        onDownload={downloadLegalDocFile}
        downloadDisabled={!legalDocPresent || legalUploading}
        details={[
          { label: 'Nome', value: legalDocName || '-' },
          { label: 'Formato', value: legalDocFormat || '-' }
        ]}
      />

      <StyledCard>
        <CardContent>
          <Grid
            container
            spacing={2}
            sx={{
              mb: 3,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              '&::-webkit-scrollbar': { height: 6 }
            }}
          >
            <Grid item sx={{ minWidth: 360, flex: '0 0 auto' }}>
              <TextField
                placeholder="Cerca per code o label..."
                variant="outlined"
                size="small"
                fullWidth
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item sx={{ minWidth: 160, flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
              <FormControlLabel control={<Checkbox checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} />} label="Solo attivi" />
            </Grid>
          </Grid>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 110 }}>Ordine</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 90, textAlign: 'center' }}>Obbl.</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 90, textAlign: 'center' }}>Attivo</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120, textAlign: 'right' }}>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tipiLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 6 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : pagedTipi.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 6 }}>
                      <Typography align="center" color="text.secondary">
                        Nessun record trovato
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedTipi.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Paper
                          variant="outlined"
                          sx={(theme) => ({
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.25,
                            py: 0.5,
                            borderRadius: '999px',
                            fontWeight: 700,
                            fontSize: 12,
                            bgcolor: theme.palette.mode === 'dark' ? alpha('#0b1220', 0.6) : '#f8fafc'
                          })}
                        >
                          {row.code}
                        </Paper>
                      </TableCell>
                      <TableCell>{row.label}</TableCell>
                      <TableCell>{Number(row.sort_order || 0)}</TableCell>
                      <TableCell align="center">
                        <Checkbox checked={Boolean(row.required)} disabled />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox checked={Boolean(row.active)} disabled />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Modifica" arrow>
                          <span>
                            <IconButton size="small" onClick={() => openEditTipo(row)}>
                              <Pencil size={16} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredTipi.length}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const next = parseInt(e.target.value, 10) || 25;
              setRowsPerPage(next);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </StyledCard>

      <Dialog open={tipoDialogOpen} onClose={closeTipoDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {Number.isInteger(tipoForm?.id) && tipoForm.id > 0 ? 'Modifica tipo' : 'Nuovo tipo'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField
                label="Code"
                size="small"
                fullWidth
                value={tipoForm.code}
                disabled={Number.isInteger(tipoForm?.id) && tipoForm.id > 0}
                onChange={(e) => setTipoForm((prev) => ({ ...prev, code: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField label="Label" size="small" fullWidth value={tipoForm.label} onChange={(e) => setTipoForm((prev) => ({ ...prev, label: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Ordine"
                size="small"
                fullWidth
                type="number"
                value={Number.isFinite(Number(tipoForm.sort_order)) ? Number(tipoForm.sort_order) : 0}
                onChange={(e) => setTipoForm((prev) => ({ ...prev, sort_order: Number(e.target.value || 0) }))}
              />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel control={<Checkbox checked={Boolean(tipoForm.required)} onChange={(e) => setTipoForm((prev) => ({ ...prev, required: e.target.checked }))} />} label="Obbligatorio" />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel control={<Checkbox checked={Boolean(tipoForm.active)} onChange={(e) => setTipoForm((prev) => ({ ...prev, active: e.target.checked }))} />} label="Attivo" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeTipoDialog} disabled={tipoSaving}>
            <X className="mr-2 h-4 w-4" />
            Annulla
          </Button>
          <Button onClick={saveTipo} disabled={tipoSaving}>
            <Check className="mr-2 h-4 w-4" />
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
